import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, FileText, Activity, Clock, TrendingUp
} from 'lucide-react';
import { LoadingSpinner } from '../components/ui';
import type { TabType } from './reporting/types';
import { useReportingData } from '../hooks/useReportingData';
import { useReportExports } from '../hooks/useReportExports';
import AnalyticsTab from './reporting/AnalyticsTab';
import RentabiliteTab from './reporting/RentabiliteTab';
import PayeTab from './reporting/PayeTab';
import HistoriqueTab from './reporting/HistoriqueTab';

export default function AdminReporting() {
  const navigate = useNavigate();
  const reportingData = useReportingData();
  const {
    activeTab, setActiveTab, loading,
    filterMode, setFilterMode, selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth, selectedWeek, setSelectedWeek,
    startDate, endDate, periodLabel, dbPeriodStr,
    handlePrevious, handleNext, handleToday,
    analytics, payrollData, projectStats, projectHistory, projectFinancials,
    projectList, employeeList,
    selectedProject, setSelectedProject, selectedEmployee, setSelectedEmployee,
    rentabiliteFilter, setRentabiliteFilter, rentaSearch, setRentaSearch,
    historyProject, setHistoryProject, historyLoading,
    historyViewMode, setHistoryViewMode, showCumulative, setShowCumulative,
    editingDevis, setEditingDevis, devisInputValue, setDevisInputValue,
    savingDevis, handleSaveDevis, resetHistoryView,
    historyChartData, aggregateByYear,
  } = reportingData;

  const { handleExportPDFAnalytics, downloadXLSX, downloadPayrollPDF, handleExportHistoryPDF } = useReportExports({
    analytics, payrollData, projectHistory, projectFinancials, projectStats,
    projectList, employeeList,
    selectedProject, selectedEmployee,
    periodLabel, dbPeriodStr, startDate, endDate,
    filterMode, selectedYear, selectedMonth, selectedWeek,
    historyViewMode, showCumulative, aggregateByYear,
  });

  // --- CHARTS DATA ---
  const doughnutData = {
    labels: ['Bureau d\'Étude', 'Suivi', 'Mise en Service', 'Chantier'],
    datasets: [{
      data: [analytics.global.etude, analytics.global.suivi, analytics.global.mise_en_service, analytics.global.chantier],
      backgroundColor: ['#3b82f6', '#f59e0b', '#8b5cf6', '#10b981'],
      borderWidth: 0,
    }],
  };

  const tabs: { id: TabType, label: string, icon: React.ReactNode, color: string, bg: string }[] = [
    { id: 'analytics', label: 'Analyses & Graphiques', icon: <Activity size={18} />, color: '#ea580c', bg: '#fff7ed' },
    { id: 'rentabilite', label: 'Rentabilité Chantiers', icon: <BarChart3 size={18} />, color: '#7c3aed', bg: '#f5f3ff' },
    { id: 'paie', label: 'Exports Paie', icon: <FileText size={18} />, color: '#2563eb', bg: '#eff6ff' },
    { id: 'historique', label: 'Historique Affaires', icon: <Clock size={18} />, color: '#059669', bg: '#ecfdf5' }
  ];

  // PeriodSelector props
  const periodSelectorProps = {
    filterMode, setFilterMode,
    selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth,
    selectedWeek, setSelectedWeek,
    handlePrevious, handleNext, handleToday,
    periodLabel
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', paddingBottom: '80px', minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>

      {/* HEADER */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        padding: '16px 24px',
        marginBottom: '24px'
      }}>
        <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: '#f1f5f9', border: 'none', color: '#64748b',
              cursor: 'pointer', fontWeight: '500', padding: '10px 14px',
              borderRadius: '10px', fontSize: '0.9rem'
            }}
          >
            <ArrowLeft size={18} /> Retour
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '10px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '12px', color: 'white' }}>
              <TrendingUp size={22} />
            </div>
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>Reporting & Analyse</h1>
              <p style={{ color: '#64748b', margin: 0, fontSize: '0.85rem' }}>Tableaux de bord et exports</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '0 24px' }}>

        {/* NAVIGATION TABS */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 24px', borderRadius: '12px', border: '1px solid',
                borderColor: activeTab === tab.id ? 'transparent' : '#e2e8f0',
                background: activeTab === tab.id ? tab.bg : 'white',
                color: activeTab === tab.id ? tab.color : '#64748b',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                boxShadow: activeTab === tab.id ? `0 4px 6px -1px ${tab.color}20` : 'none'
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner message="Chargement des données..." />
        ) : (
          <>
            {activeTab === 'analytics' && (
              <AnalyticsTab
                analytics={analytics}
                selectedProject={selectedProject}
                setSelectedProject={setSelectedProject}
                selectedEmployee={selectedEmployee}
                setSelectedEmployee={setSelectedEmployee}
                projectList={projectList}
                employeeList={employeeList}
                handleExportPDFAnalytics={handleExportPDFAnalytics}
                doughnutData={doughnutData}
                periodSelectorProps={periodSelectorProps}
              />
            )}

            {activeTab === 'rentabilite' && (
              <RentabiliteTab
                projectStats={projectStats}
                rentabiliteFilter={rentabiliteFilter}
                setRentabiliteFilter={setRentabiliteFilter}
                rentaSearch={rentaSearch}
                setRentaSearch={setRentaSearch}
                setHistoryProject={setHistoryProject}
                setActiveTab={setActiveTab}
              />
            )}

            {activeTab === 'paie' && (
              <PayeTab
                payrollData={payrollData}
                filterMode={filterMode}
                setFilterMode={setFilterMode}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                selectedMonth={selectedMonth}
                setSelectedMonth={setSelectedMonth}
                selectedWeek={selectedWeek}
                setSelectedWeek={setSelectedWeek}
                handlePrevious={handlePrevious}
                handleNext={handleNext}
                handleToday={handleToday}
                periodLabel={periodLabel}
                downloadPayrollPDF={downloadPayrollPDF}
                downloadXLSX={downloadXLSX}
              />
            )}

            {activeTab === 'historique' && (
              <HistoriqueTab
                projectStats={projectStats}
                projectHistory={projectHistory}
                projectFinancials={projectFinancials}
                historyProject={historyProject}
                setHistoryProject={setHistoryProject}
                resetHistoryView={resetHistoryView}
                historyViewMode={historyViewMode}
                setHistoryViewMode={setHistoryViewMode}
                showCumulative={showCumulative}
                setShowCumulative={setShowCumulative}
                historyLoading={historyLoading}
                editingDevis={editingDevis}
                setEditingDevis={setEditingDevis}
                devisInputValue={devisInputValue}
                setDevisInputValue={setDevisInputValue}
                savingDevis={savingDevis}
                handleSaveDevis={handleSaveDevis}
                historyChartData={historyChartData}
                aggregateByYear={aggregateByYear}
                handleExportHistoryPDF={handleExportHistoryPDF}
                projectList={projectList}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
